import { Fragment, type JSX } from "react";
import { Arrow, Circle, Group, Line } from "react-konva";

import type { TokenTrail } from "./use-playback.js";

interface TrailLayerProps {
  trails: TokenTrail[];
}

const COLOR: Record<string, string> = {
  player: "#97fc00",
  opponent: "#ff798f",
  ball: "#ffb02e",
};

/**
 * Renders dashed Laufweg-Spuren for every token across all frames.
 * Each waypoint gets a small marker. The final segment is an arrow head so
 * the direction of motion is visually obvious. Rendered as a non-listening
 * overlay below the tokens layer.
 */
export function TrailLayer({ trails }: TrailLayerProps): JSX.Element {
  return (
    <Group listening={false}>
      {trails.map((trail) => {
        const color = COLOR[trail.kind] ?? "#97fc00";
        const points = trail.points;
        // Pre-final polyline (waypoints, excluding last segment which becomes the arrow)
        const headStartIdx = points.length - 4;
        const polyPoints = points.slice(0, headStartIdx + 2);
        const arrowPoints = points.slice(headStartIdx);
        const waypoints: { x: number; y: number }[] = [];
        for (let i = 0; i < points.length - 2; i += 2) {
          const x = points[i];
          const y = points[i + 1];
          if (x !== undefined && y !== undefined) waypoints.push({ x, y });
        }
        return (
          <Fragment key={trail.tokenId}>
            {polyPoints.length >= 4 && (
              <Line
                points={polyPoints}
                stroke={color}
                strokeWidth={3}
                dash={[10, 8]}
                opacity={0.55}
                lineCap="round"
                lineJoin="round"
              />
            )}
            {arrowPoints.length >= 4 && (
              <Arrow
                points={arrowPoints}
                stroke={color}
                fill={color}
                strokeWidth={3}
                dash={[10, 8]}
                opacity={0.7}
                pointerLength={18}
                pointerWidth={16}
                lineCap="round"
                lineJoin="round"
              />
            )}
            {waypoints.map((w, i) => (
              <Circle
                key={i}
                x={w.x}
                y={w.y}
                radius={6}
                fill={color}
                opacity={0.45}
                stroke="#101418"
                strokeWidth={1.5}
              />
            ))}
          </Fragment>
        );
      })}
    </Group>
  );
}
